import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import Icons from "../icons";

/* The Jira project (space) picker of the Requirement Board and the Artifacts
 * screen, in place of a native <select>: the key as a chip and the name, a
 * check on the chosen one, and a filter once there are more than a few.
 *
 * A listbox: the button opens it (click, Enter, Space, ↓ / ↑), ↑ ↓ Home End
 * move, Enter picks, Escape or a click outside closes and gives the focus back.
 *
 * Props:
 *  - projects: [{ key, name }]
 *  - value: the chosen key
 *  - onChange(key): only when a different project is picked
 *  - disabled, placeholder ("Loading spaces…" while there are none)
 *  - label: the accessible name ("Jira project")
 */
const FILTER_FROM = 7;

export default function ProjectSelect({ projects = [], value, onChange, disabled = false, placeholder = "Choose a project", label = "Jira project" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const filterRef = useRef(null);
  const id = useId();
  const listId = `${id}-list`;

  const selected = projects.find((p) => p.key === value);
  const showFilter = projects.length >= FILTER_FROM;
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => p.key.toLowerCase().includes(q) || (p.name || "").toLowerCase().includes(q));
  }, [projects, query]);

  const openList = () => {
    if (disabled || !projects.length) return;
    setQuery("");
    setActive(Math.max(0, projects.findIndex((p) => p.key === value)));
    setOpen(true);
  };
  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  };
  const pick = (p) => {
    if (!p) return;
    close();
    if (p.key !== value) onChange?.(p.key);
  };

  // Focus the filter (or the list) once open; keep the active option in view.
  useEffect(() => {
    if (!open) return;
    (showFilter ? filterRef.current : listRef.current)?.focus();
  }, [open, showFilter]);
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [open, active]);
  useEffect(() => setActive(0), [query]);

  // A click outside closes it, without taking the focus back.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const onListKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(shown.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActive(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActive(shown.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(shown[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "Tab") {
      close(false);
    }
  };

  const onButtonKey = (e) => {
    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
      e.preventDefault();
      openList();
    }
  };

  const activeId = shown[active] ? `${id}-opt-${shown[active].key}` : undefined;

  return (
    <div ref={rootRef} className="relative flex-none">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => (open ? close() : openList())}
        onKeyDown={onButtonKey}
        disabled={disabled || !projects.length}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={selected ? `${label}: ${selected.key} — ${selected.name}` : label}
        title={selected ? `${selected.key} — ${selected.name}` : label}
        className={[
          "flex h-[2.375rem] max-w-[22rem] items-center gap-2 rounded-lg border bg-card pl-2 pr-2.5 text-left text-sm shadow-sm transition",
          "focus:outline-none focus-visible:border-brand-500 focus-visible:ring-1 focus-visible:ring-brand-500",
          open ? "border-brand-500 ring-1 ring-brand-500" : "border-slate-200 hover:border-slate-300",
          "disabled:cursor-not-allowed disabled:opacity-60",
        ].join(" ")}
      >
        {selected ? (
          <>
            <span className="flex-none rounded-md bg-brand-50 px-1.5 py-0.5 font-mono text-xs font-semibold text-brand-700">
              {selected.key}
            </span>
            <span className="min-w-0 truncate text-slate-900">{selected.name}</span>
          </>
        ) : (
          <span className="min-w-0 truncate px-1 text-slate-400">{projects.length ? placeholder : value || placeholder}</span>
        )}
        <Icons.ChevronDown
          className={`ml-auto h-4 w-4 flex-none text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-40 mt-1.5 w-[24rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-slate-200">
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
            <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
            <span className="text-[0.6875rem] tabular-nums text-slate-400">{projects.length}</span>
          </div>
          {showFilter && (
            <div className="border-b border-slate-100 p-2">
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500">
                <Icons.Search className="h-4 w-4 flex-none text-slate-400" aria-hidden="true" />
                <input
                  ref={filterRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onListKey}
                  placeholder="Find a project…"
                  aria-label="Find a project"
                  aria-controls={listId}
                  aria-activedescendant={activeId}
                  className="h-8 w-full bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
                />
              </div>
            </div>
          )}
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={label}
            aria-activedescendant={activeId}
            tabIndex={-1}
            onKeyDown={onListKey}
            className="cq-scroll max-h-80 overflow-y-auto p-1.5 focus:outline-none"
          >
            {shown.length === 0 && <li className="px-3 py-6 text-center text-sm text-slate-400">No project matches.</li>}
            {shown.map((p, i) => {
              const isSel = p.key === value;
              const isActive = i === active;
              return (
                <li
                  key={p.key}
                  id={`${id}-opt-${p.key}`}
                  data-index={i}
                  role="option"
                  aria-selected={isSel}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(p)}
                  className={[
                    "flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm",
                    isActive ? "bg-brand-50" : "",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "flex-none rounded-md px-1.5 py-0.5 font-mono text-xs font-semibold",
                      isSel ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-600",
                    ].join(" ")}
                  >
                    {p.key}
                  </span>
                  <span className={`min-w-0 flex-1 truncate ${isSel ? "font-semibold text-brand-800" : "text-slate-800"}`}>{p.name}</span>
                  {isSel && <Icons.Check className="h-4 w-4 flex-none text-brand-600" aria-hidden="true" />}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
